import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import apiConfig from '../../config/apiConfig';
import { NotificationItem, NotificationsResponse } from '../types/notification.types';

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('authToken');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axios.get<NotificationsResponse>(
        `${apiConfig.apiBaseURL}/notifications`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Handle the actual API response structure
      if (response.data.status === 'Success' && response.data.data) {
        const { notifications: notificationList, unreadCount: count } = response.data.data;

        // Map API response to expected frontend structure
        const mappedNotifications = notificationList.map((notification: any) => ({
          id: notification._id || notification.id,
          title: notification.title || notification.subject || 'Notification',
          message: notification.message || notification.content || notification.body || '',
          type: notification.type || 'info',
          isRead: notification.isRead || notification.read || false,
          createdAt: notification.createdAt || notification.created_at || new Date().toISOString(),
          updatedAt: notification.updatedAt || notification.updated_at || new Date().toISOString(),
          // Include navigation-related fields - try various possible field names
          ticketId: notification.ticketId || notification.ticket_id || notification.relatedTicket ||
                   notification.ticketRef || notification.supportTicketId || notification.reference_id ||
                   // NEW: Use relatedId when relatedType indicates it's a ticket
                   (notification.relatedType === 'support_ticket' || notification.relatedType === 'ticket' ||
                    notification.type === 'support_reply' || notification.type === 'support_ticket' ||
                    notification.type?.includes('support') || notification.type?.includes('ticket') ?
                    notification.relatedId : null),
          relatedTicketId: notification.relatedTicketId || notification.related_ticket_id ||
                          notification.parentTicketId || notification.originalTicketId,
          // Also store the original relatedId and relatedType for debugging
          relatedId: notification.relatedId,
          relatedType: notification.relatedType,
          giftId: notification.giftId || notification.gift_id || notification.relatedGift ||
                 notification.giftRef || notification.giftItemId,
          relatedGiftId: notification.relatedGiftId || notification.related_gift_id,
          contactId: notification.contactId || notification.contact_id || notification.relatedContact ||
                    notification.contactRef || notification.userId,
          relatedContactId: notification.relatedContactId || notification.related_contact_id
        }));

        // Sort notifications by creation date (newest first)
        const sortedNotifications = mappedNotifications.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setNotifications(sortedNotifications);
        setUnreadCount(count);
      } else {
        setError(response.data.message || 'Failed to fetch notifications');
      }
    } catch (error: any) {
      console.error('Error fetching notifications:', error);
      if (error.response?.status === 401) {
        setError('Authentication failed');
      } else if (error.response?.status === 404) {
        // If endpoint doesn't exist yet, just set empty state
        setNotifications([]);
        setUnreadCount(0);
      } else {
        setError('Failed to load notifications');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Mark a single notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      await axios.patch(
        `${apiConfig.apiBaseURL}/notifications/${notificationId}/read`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Update local state
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === notificationId
            ? { ...notification, isRead: true }
            : notification
        )
      );

      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      await axios.patch(
        `${apiConfig.apiBaseURL}/notifications/mark-all-read`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Update local state
      setNotifications(prev =>
        prev.map(notification => ({ ...notification, isRead: true }))
      );

      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, []);

  // Refresh notifications
  const refreshNotifications = useCallback(async () => {
    await fetchNotifications();
  }, [fetchNotifications]);

  // Poll for new notifications every 2 minutes
  useEffect(() => {
    // Initial fetch
    fetchNotifications();

    // Set up polling interval
    const intervalId = setInterval(() => {
      fetchNotifications();
    }, 2 * 60 * 1000); // 2 minutes

    return () => clearInterval(intervalId);
  }, [fetchNotifications]);

  // Listen for focus events to refresh notifications when user returns to tab
  useEffect(() => {
    const handleFocus = () => {
      fetchNotifications();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    refreshNotifications,
    fetchNotifications
  };
};